import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/axios';
import websocketService from '../services/websocketService';

const PlanContext = createContext();

// Cache management utilities
const CACHE_KEY = 'velox_plan_cache';
const CACHE_VERSION = '1.0';
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

const cacheManager = {
  // Get cached plan data
  getCachedPlan: () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      
      const data = JSON.parse(cached);
      
      // Check if cache is expired
      if (Date.now() - data.timestamp > CACHE_EXPIRY) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      
      // Check cache version
      if (data.version !== CACHE_VERSION) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      
      return data.planData;
    } catch (error) {
      console.error('Error reading plan cache:', error);
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  },

  // Set cached plan data
  setCachedPlan: (planData) => {
    try {
      const cacheData = {
        planData,
        timestamp: Date.now(),
        version: CACHE_VERSION
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error setting plan cache:', error);
    }
  },

  // Invalidate cache
  invalidateCache: () => {
    try {
      localStorage.removeItem(CACHE_KEY);
      console.log('Plan cache invalidated');
    } catch (error) {
      console.error('Error invalidating plan cache:', error);
    }
  },

  // Clear all plan-related cache
  clearAllPlanCache: () => {
    try {
      // Remove plan cache
      localStorage.removeItem(CACHE_KEY);
      
      // Remove user cache that might contain plan data
      localStorage.removeItem('user');
      
      // Remove any other plan-related cache
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes('plan') || key.includes('billing')) {
          localStorage.removeItem(key);
        }
      });
      
      console.log('All plan-related cache cleared');
    } catch (error) {
      console.error('Error clearing plan cache:', error);
    }
  }
};

export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
};

export const PlanProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [planType, setPlanType] = useState('Free');
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastPlanUpdate, setLastPlanUpdate] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const hasInitialized = useRef(false);

  // WebSocket event handlers
  const handlePlanUpdate = useCallback((planData) => {
    console.log('PlanContext: Received real-time plan update:', planData);
    
    setPlanType(planData.planName);
    setPlanData(planData);
    setLastPlanUpdate(new Date());
    
    // Invalidate cache and update with new data
    cacheManager.invalidateCache();
    cacheManager.setCachedPlan(planData);
    
    // Show notification to user
    if (window.Notification && Notification.permission === 'granted') {
      new Notification('Plan Updated', {
        body: `Your plan has been updated to ${planData.planName}`,
        icon: '/Velox-AI-logo.png'
      });
    }
  }, []);

  const handleSubscriptionUpdate = useCallback((subscriptionData) => {
    console.log('PlanContext: Received subscription update:', subscriptionData);
    
    // Update plan data with subscription status
    setPlanData(prev => ({
      ...prev,
      status: subscriptionData.status,
      warning: subscriptionData.warning,
      nextBillingDate: subscriptionData.nextBillingDate
    }));
    
    // Show notification for important subscription changes
    if (subscriptionData.status === 'expired') {
      if (window.Notification && Notification.permission === 'granted') {
        new Notification('Subscription Expired', {
          body: 'Your subscription has expired. Please renew to continue.',
          icon: '/Velox-AI-logo.png'
        });
      }
    }
  }, []);

  const handleBillingNotification = useCallback((billingData) => {
    console.log('PlanContext: Received billing notification:', billingData);
    
    // Handle billing notifications (payment due, payment failed, etc.)
    if (window.Notification && Notification.permission === 'granted') {
      new Notification('Billing Update', {
        body: billingData.message || 'You have a billing update',
        icon: '/Velox-AI-logo.png'
      });
    }
  }, []);

  const handleWsConnected = useCallback(() => {
    console.log('PlanContext: WebSocket connected');
    setWsConnected(true);
    
    // Subscribe to plan updates
    websocketService.subscribeToPlanUpdates();
  }, []);

  const handleWsDisconnected = useCallback(() => {
    console.log('PlanContext: WebSocket disconnected');
    setWsConnected(false);
  }, []);

  // Initialize WebSocket connection
  const initializeWebSocket = useCallback(() => {
    if (user && user.id) {
      console.log('PlanContext: Initializing WebSocket connection');
      
      // Set up event listeners
      websocketService.on('connected', handleWsConnected);
      websocketService.on('disconnected', handleWsDisconnected);
      websocketService.on('plan_updated', handlePlanUpdate);
      websocketService.on('subscription_updated', handleSubscriptionUpdate);
      websocketService.on('billing_notification', handleBillingNotification);
      
      // Connect to WebSocket
      websocketService.connect(user.id).catch(error => {
        console.error('Failed to connect to WebSocket:', error);
      });
    }
  }, [user, handleWsConnected, handleWsDisconnected, handlePlanUpdate, handleSubscriptionUpdate, handleBillingNotification]);

  // Clean up WebSocket connection
  const cleanupWebSocket = useCallback(() => {
    websocketService.off('connected', handleWsConnected);
    websocketService.off('disconnected', handleWsDisconnected);
    websocketService.off('plan_updated', handlePlanUpdate);
    websocketService.off('subscription_updated', handleSubscriptionUpdate);
    websocketService.off('billing_notification', handleBillingNotification);
    
    websocketService.disconnect();
    setWsConnected(false);
  }, [handleWsConnected, handleWsDisconnected, handlePlanUpdate, handleSubscriptionUpdate, handleBillingNotification]);

  // Fetch user's plan information with cache management
  const fetchPlanData = useCallback(async (forceRefresh = false) => {
    console.log('PlanContext: Fetching plan data for user:', user.email);
    console.log('PlanContext: Force refresh:', forceRefresh);
    
    // Check cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cachedPlan = cacheManager.getCachedPlan();
      if (cachedPlan) {
        console.log('PlanContext: Using cached plan data:', cachedPlan.planName);
        setPlanType(cachedPlan.planName);
        setPlanData(cachedPlan);
        setLoading(false);
        hasInitialized.current = true;
        return;
      }
    }
    
    // Use user's plan data directly from auth context
    if (user && user.currentPlanName) {
      console.log('PlanContext: Using user plan data:', user.currentPlanName);
      console.log('PlanContext: Setting planType to:', user.currentPlanName);
      
      const newPlanData = {
        planName: user.currentPlanName,
        planType: user.currentPlanType || 'Monthly',
        price: user.currentPlanName === 'Free' ? 0 : 
               user.currentPlanName === 'Individual' ? 20 : 48,
        status: 'active',
        lastUpdated: new Date().toISOString()
      };
      
      setPlanType(user.currentPlanName);
      setPlanData(newPlanData);
      setLastPlanUpdate(new Date());
      
      // Cache the plan data
      cacheManager.setCachedPlan(newPlanData);
    } else {
      console.log('PlanContext: No user plan data, defaulting to Free');
      const freePlanData = {
        planName: 'Free',
        planType: 'Monthly',
        price: 0,
        status: 'active',
        lastUpdated: new Date().toISOString()
      };
      
      setPlanType('Free');
      setPlanData(freePlanData);
      setLastPlanUpdate(new Date());
      
      // Cache the plan data
      cacheManager.setCachedPlan(freePlanData);
    }
    
    // Set loading to false after setting the plan data
    setLoading(false);
    hasInitialized.current = true;
  }, [user]);

  // Invalidate cache when plan changes
  const invalidatePlanCache = useCallback(() => {
    cacheManager.invalidateCache();
    setLastPlanUpdate(new Date());
  }, []);

  // Clear all plan cache
  const clearPlanCache = useCallback(() => {
    cacheManager.clearAllPlanCache();
    setLastPlanUpdate(new Date());
  }, []);

  useEffect(() => {
    console.log('PlanContext: useEffect triggered, user:', user ? user.email : 'null');
    console.log('PlanContext: authLoading:', authLoading);
    console.log('PlanContext: hasInitialized:', hasInitialized.current);
    
    // Only fetch plan data ONCE when we have a user and auth is not loading
    // AND we haven't initialized yet
    if (user && !authLoading && !hasInitialized.current) {
      console.log('PlanContext: Calling fetchPlanData for first time');
      fetchPlanData();
      
      // Initialize WebSocket connection
      initializeWebSocket();
    } else if (!user) {
      console.log('PlanContext: No user, resetting state');
      setLoading(false);
      hasInitialized.current = false;
      setPlanType('Free');
      setPlanData({
        planName: 'Free',
        planType: 'Monthly',
        price: 0,
        status: 'active',
        lastUpdated: new Date().toISOString()
      });
      // Clear cache when user logs out
      cacheManager.clearAllPlanCache();
      
      // Clean up WebSocket connection
      cleanupWebSocket();
    }
  }, [user, authLoading, fetchPlanData, initializeWebSocket, cleanupWebSocket]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      cleanupWebSocket();
    };
  }, [cleanupWebSocket]);

  // Plan-based feature limits
  const getPlanLimits = () => {
    switch (planType) {
      case 'Free':
        return {
          documentCreations: 5,
          chats: 10,
          chatQueries: 50, // Updated to reflect 10 chats * 5 messages
          documentUploads: 5,
          integrations: [],
          aiModels: ['gpt-3.5-turbo'],
          customTemplates: false,
          teamFeatures: false,
          prioritySupport: false,
          radarCompanies: 250,
          radarContacts: 1000,
          radarTranscripts: 100
        };
      case 'Individual':
        return {
          documentCreations: -1, // unlimited
          chats: -1, // unlimited
          chatQueries: -1, // unlimited
          documentUploads: -1, // unlimited
          integrations: ['slack'],
          aiModels: ['gpt-3.5-turbo', 'gpt-4'],
          customTemplates: false,
          teamFeatures: false,
          prioritySupport: true,
          radarCompanies: -1, // unlimited
          radarContacts: -1, // unlimited
          radarTranscripts: -1 // unlimited
        };
      case 'Team':
        return {
          documentCreations: -1, // unlimited
          chats: -1, // unlimited
          chatQueries: -1, // unlimited
          documentUploads: -1, // unlimited
          integrations: ['slack', 'hubspot'],
          aiModels: ['gpt-4.1', 'gpt-4o', 'claude-sonnet-3.5', 'claude-sonnet-4'],
          modelSelection: true, // New feature flag
          customTemplates: true,
          teamFeatures: true,
          prioritySupport: true,
          radarCompanies: -1, // unlimited
          radarContacts: -1, // unlimited
          radarTranscripts: -1 // unlimited
        };
      default:
        return {
          documentCreations: 5,
          chats: 10,
          chatQueries: 50, // Updated to reflect 10 chats * 5 messages
          documentUploads: 5,
          integrations: [],
          aiModels: ['gpt-3.5-turbo'],
          customTemplates: false,
          teamFeatures: false,
          prioritySupport: false,
          radarCompanies: 250,
          radarContacts: 1000,
          radarTranscripts: 100
        };
    }
  };

  // Check if a feature is available for the current plan
  const isFeatureAvailable = (feature) => {
    const limits = getPlanLimits();
    return limits[feature] !== undefined && limits[feature] !== false;
  };

  // Check if user has unlimited access to a feature
  const isUnlimited = (feature) => {
    const limits = getPlanLimits();
    return limits[feature] === -1;
  };

  // Get the limit for a specific feature
  const getFeatureLimit = (feature) => {
    const limits = getPlanLimits();
    return limits[feature] || 0;
  };

  // Update plan data (for when user upgrades/downgrades)
  const updatePlanData = (newPlanData) => {
    console.log('PlanContext: Updating plan data:', newPlanData);
    setPlanType(newPlanData.planName);
    setPlanData(newPlanData);
    setLastPlanUpdate(new Date());
    
    // Invalidate cache when plan changes
    cacheManager.invalidateCache();
    
    // Cache the new plan data
    cacheManager.setCachedPlan(newPlanData);
  };

  // Force refresh plan data from server - only called from settings or billing
  const refreshPlanData = async () => {
    console.log('PlanContext: Force refreshing plan data');
    setLoading(true);
    try {
      // Check for expired subscription first
      try {
        await api.post('/payments/check-expiration');
      } catch (error) {
        console.log('Subscription check failed, continuing with plan refresh:', error);
      }

      // Refresh user data from server to get updated plan
      const response = await api.get('/auth/me');
      const userData = response.data.data.user;
      console.log('PlanContext: Successfully refreshed user data:', userData.currentPlanName);
      
      const newPlanData = {
        planName: userData.currentPlanName || 'Free',
        planType: userData.currentPlanType || 'Monthly',
        price: userData.currentPlanName === 'Free' ? 0 : 
               userData.currentPlanName === 'Individual' ? 20 : 48,
        status: 'active',
        lastUpdated: new Date().toISOString()
      };
      
      setPlanType(newPlanData.planName);
      setPlanData(newPlanData);
      setLastPlanUpdate(new Date());
      
      // Invalidate old cache and set new cache
      cacheManager.invalidateCache();
      cacheManager.setCachedPlan(newPlanData);
      
    } catch (error) {
      console.error('Error refreshing plan data:', error);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    planType,
    planData,
    loading,
    lastPlanUpdate,
    wsConnected,
    getPlanLimits,
    isFeatureAvailable,
    isUnlimited,
    getFeatureLimit,
    updatePlanData,
    fetchPlanData,
    refreshPlanData,
    invalidatePlanCache,
    clearPlanCache
  };

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
}; 